import type { Permission } from '@hostesa/auth-client';
import type { FeatureFlag } from '@hostesa/feature-flags';

/**
 * Declarative platform navigation. Each item is gated by an RBAC permission
 * and optionally a feature flag. Visibility here is a UX layer only — the
 * route guards (RBAC + ABAC + flag) and the backend remain authoritative.
 *
 * Groups mirror the brand's hotel-department model:
 * Reception · Hospitality · Intelligence · Settings.
 */

export type NavGroupId = 'reception' | 'hospitality' | 'intelligence' | 'settings';

export interface NavItemConfig {
  /** Stable id — also used for the data-testid and the route path segment. */
  id: string;
  /** Path segment under `/$tenantSlug/$hotelSlug/`. */
  segment: string;
  /** i18n key under the `platform:nav` namespace. */
  labelKey: string;
  /** RBAC permission required to see/reach the item. */
  permission: Permission;
  /** Optional feature flag that must be enabled. */
  flag?: FeatureFlag;
}

export interface NavGroupConfig {
  id: NavGroupId;
  /** i18n key under `platform:navGroups`. */
  labelKey: string;
  items: NavItemConfig[];
}

export const NAV_GROUPS: readonly NavGroupConfig[] = [
  {
    id: 'reception',
    labelKey: 'navGroups.reception',
    items: [
      { id: 'reception', segment: 'reception', labelKey: 'nav.reception', permission: 'call:read' },
      {
        id: 'live-calls',
        segment: 'live-calls',
        labelKey: 'nav.liveCalls',
        permission: 'call:read',
      },
      { id: 'calls', segment: 'calls', labelKey: 'nav.calls', permission: 'call:read' },
      {
        id: 'conversations',
        segment: 'conversations',
        labelKey: 'nav.conversations',
        permission: 'conversation:read',
      },
    ],
  },
  {
    id: 'hospitality',
    labelKey: 'navGroups.hospitality',
    items: [
      {
        id: 'reservations',
        segment: 'reservations',
        labelKey: 'nav.reservations',
        permission: 'reservation:read',
      },
      {
        id: 'bookings',
        segment: 'bookings',
        labelKey: 'nav.bookings',
        permission: 'booking:read',
      },
      {
        id: 'tasks',
        segment: 'tasks',
        labelKey: 'nav.tasks',
        permission: 'service_request:read',
        flag: 'in_stay_operations',
      },
    ],
  },
  {
    id: 'intelligence',
    labelKey: 'navGroups.intelligence',
    items: [
      {
        id: 'knowledge',
        segment: 'knowledge',
        labelKey: 'nav.knowledge',
        permission: 'knowledge:read',
        flag: 'knowledge_onboarding',
      },
      {
        id: 'onboarding',
        segment: 'onboarding',
        labelKey: 'nav.onboarding',
        permission: 'onboarding:read',
        flag: 'knowledge_onboarding',
      },
      {
        id: 'analytics',
        segment: 'analytics',
        labelKey: 'nav.analytics',
        permission: 'call:read',
      },
      {
        id: 'usage',
        segment: 'usage',
        labelKey: 'nav.usage',
        permission: 'usage:read',
        flag: 'usage_reporting',
      },
    ],
  },
  {
    id: 'settings',
    labelKey: 'navGroups.settings',
    items: [
      {
        id: 'hotel-setup',
        segment: 'hotel-setup',
        labelKey: 'nav.hotelSetup',
        permission: 'hotel:read',
      },
      {
        id: 'hotel-profile',
        segment: 'hotel-profile',
        labelKey: 'nav.hotelProfile',
        permission: 'hotel:read',
      },
      { id: 'team', segment: 'team', labelKey: 'nav.team', permission: 'user:read' },
      { id: 'scripts', segment: 'scripts', labelKey: 'nav.scripts', permission: 'hotel:read' },
      {
        id: 'handoff-rules',
        segment: 'handoff-rules',
        labelKey: 'nav.handoffRules',
        permission: 'hotel:read',
      },
      {
        id: 'integrations',
        segment: 'integrations',
        labelKey: 'nav.integrations',
        permission: 'integration:read',
        flag: 'integrations_management',
      },
      { id: 'settings', segment: 'settings', labelKey: 'nav.settings', permission: 'hotel:read' },
    ],
  },
] as const;
